import { ReactNode, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

const modalRoot = document.querySelector('#modal-root')! as HTMLDivElement;
const appRoot = document.querySelector('#app-root')! as HTMLDivElement;

let modalCount = 0;
let previouslyFocussed: HTMLElement | undefined;
let itemBeforeOpen: HTMLElement | undefined;

const revertFocus = (event: FocusEvent) => {
    event.stopImmediatePropagation();
    event.preventDefault();
    const element: HTMLElement =
        previouslyFocussed ?? modalRoot.querySelector('button') ?? modalRoot;
    element.focus?.();
    return false;
};

const memoPrevious = (event: FocusEvent) => {
    previouslyFocussed = event.target as HTMLElement;
};

function modalEffect() {
    modalCount += 1;
    if (modalCount === 1) {
        itemBeforeOpen = appRoot.querySelector(':focus') as
            | HTMLElement
            | undefined;
        appRoot.addEventListener('focusin', revertFocus);
        window.addEventListener('focusin', memoPrevious);
        appRoot.classList.add('behind-modal');
    }
    return () => {
        modalCount -= 1;
        if (modalCount === 0) {
            appRoot.classList.remove('behind-modal');
            appRoot.removeEventListener('focusin', revertFocus);
            window.removeEventListener('focusin', memoPrevious);
            itemBeforeOpen?.focus();
            itemBeforeOpen = undefined;
            previouslyFocussed = undefined;
        }
    };
}

interface ModalProps {
    children?: ReactNode;
    onClose?: () => void;
}

export const Modal = ({ children, onClose }: ModalProps) => {
    const currentRoot = useMemo(() => document.createElement('div'), []);
    useEffect(modalEffect, []);
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.code === 'Escape') onClose?.();
        };
        modalRoot.append(currentRoot);
        window.addEventListener('keydown', onKeyDown);

        const element: HTMLElement | null =
            currentRoot.querySelector(
                "*[tabindex='0'], *[tabindex='1'], input, input:not([type='hidden']), button, a"
            ) ?? null;
        element?.focus();
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            modalRoot.removeChild(currentRoot);
        };
    }, [currentRoot, onClose]);
    return createPortal(<>{children}</>, currentRoot);
};
